import type {
  Task,
  Project,
  Tag,
  TaskWithInstance,
  TaskInstance,
  User,
  UserSettings,
  Memory,
  Alarm,
} from '@backend/types'

export class ApiError extends Error {
  status: number

  constructor(status: number, message?: string) {
    super(message ?? `HTTP error! status: ${status}`)
    this.name = 'ApiError'
    this.status = status
  }
}

export interface UserResponse {
  user_id: number
  name: string
  telegram_id: string
  email?: string
  settings: UserSettings
}

export class ApiService {
  private readonly baseUrl: string
  private readonly headers: HeadersInit

  constructor(endpoint: string, initDataRaw: string) {
    this.baseUrl = endpoint
    this.headers = {
      'Authorization': `tma ${initDataRaw}`,
      'Content-Type': 'application/json',
    }
  }

  // Tasks API
  async getTasks(): Promise<TaskWithInstance[]> {
    const response = await fetch(`${this.baseUrl}/api/tasks`, {
      method: 'GET',
      headers: this.headers,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  async getTask(taskId: number): Promise<TaskWithInstance> {
    const response = await fetch(`${this.baseUrl}/api/tasks/${taskId}`, {
      method: 'GET',
      headers: this.headers,
    })

    if (!response.ok) {
      throw new ApiError(response.status)
    }

    return response.json()
  }

  async createTask(task: Partial<Task>): Promise<TaskWithInstance> {
    const response = await fetch(`${this.baseUrl}/api/tasks`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(task),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  async updateTask(taskId: number, task: Partial<Task>): Promise<TaskWithInstance> {
    const response = await fetch(`${this.baseUrl}/api/tasks/${taskId}`, {
      method: 'PUT',
      headers: this.headers,
      body: JSON.stringify(task),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  async deleteTask(taskId: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/tasks/${taskId}`, {
      method: 'DELETE',
      headers: this.headers,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
  }

  // Task Instance API
  async createTaskInstance(taskId: number, instance: Partial<TaskInstance>): Promise<TaskInstance> {
    const response = await fetch(`${this.baseUrl}/api/tasks/${taskId}/instances`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(instance),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  async updateTaskInstance(instanceId: number, instance: Partial<TaskInstance>): Promise<TaskInstance> {
    const response = await fetch(`${this.baseUrl}/api/tasks/instances/${instanceId}`, {
      method: 'PUT',
      headers: this.headers,
      body: JSON.stringify(instance),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  async deleteTaskInstance(instanceId: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/tasks/instances/${instanceId}`, {
      method: 'DELETE',
      headers: this.headers,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
  }

  // Projects API
  async getProjects(): Promise<Project[]> {
    const response = await fetch(`${this.baseUrl}/api/projects`, {
      method: 'GET',
      headers: this.headers,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  async getProject(projectId: number): Promise<Project> {
    const response = await fetch(`${this.baseUrl}/api/projects/${projectId}`, {
      method: 'GET',
      headers: this.headers,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  async createProject(project: Partial<Project>): Promise<Project> {
    const response = await fetch(`${this.baseUrl}/api/projects`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(project),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  async updateProject(projectId: number, project: Partial<Project>): Promise<Project> {
    const response = await fetch(`${this.baseUrl}/api/projects/${projectId}`, {
      method: 'PUT',
      headers: this.headers,
      body: JSON.stringify(project),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  async deleteProject(projectId: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/projects/${projectId}`, {
      method: 'DELETE',
      headers: this.headers,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
  }

  // Tags API
  async getTags(): Promise<Tag[]> {
    const response = await fetch(`${this.baseUrl}/api/tags`, {
      method: 'GET',
      headers: this.headers,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  // User API
  async getUser(): Promise<UserResponse> {
    const response = await fetch(`${this.baseUrl}/api/user`, {
      method: 'GET',
      headers: this.headers,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  async updateUser(data: {name?: string; settings?: Partial<UserSettings>}): Promise<UserResponse> {
    const response = await fetch(`${this.baseUrl}/api/user`, {
      method: 'PUT',
      headers: this.headers,
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  // Memories API
  async getMemories(): Promise<Memory[]> {
    const response = await fetch(`${this.baseUrl}/api/memories`, {
      method: 'GET',
      headers: this.headers,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  async updateMemory(memoryId: number, data: {content: string; expired_at?: string | null}): Promise<Memory> {
    const response = await fetch(`${this.baseUrl}/api/memories/${memoryId}`, {
      method: 'PUT',
      headers: this.headers,
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  async deleteMemory(memoryId: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/memories/${memoryId}`, {
      method: 'DELETE',
      headers: this.headers,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
  }

  // Alarms API
  async getAlarms(): Promise<Alarm[]> {
    const response = await fetch(`${this.baseUrl}/api/alarms`, {
      method: 'GET',
      headers: this.headers,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  async updateAlarm(
    alarmId: number,
    data: {note?: string; trigger_at?: string; cron_schedule?: string | null}
  ): Promise<Alarm> {
    const response = await fetch(`${this.baseUrl}/api/alarms/${alarmId}`, {
      method: 'PUT',
      headers: this.headers,
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  async deleteAlarm(alarmId: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/alarms/${alarmId}`, {
      method: 'DELETE',
      headers: this.headers,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
  }

  // API Keys
  async getApiKeys(): Promise<{key_id: number; name: string; created_at: string; last_used_at: string | null}[]> {
    const response = await fetch(`${this.baseUrl}/api/keys`, {
      method: 'GET',
      headers: this.headers,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  async createApiKey(name: string): Promise<{key_id: number; name: string; created_at: string; key: string}> {
    const response = await fetch(`${this.baseUrl}/api/keys`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify({name}),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  async deleteApiKey(keyId: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/keys/${keyId}`, {
      method: 'DELETE',
      headers: this.headers,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
  }

  // Search API
  async searchTasks(query: string): Promise<TaskWithInstance[]> {
    const response = await fetch(`${this.baseUrl}/api/tasks/search?q=${encodeURIComponent(query)}`, {
      method: 'GET',
      headers: this.headers,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    return result.tasks
  }
}
